import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { formatOpenUntilSummary } from '@theme/store-hours';

/**
 * @typedef {Object} StoreLocationDetailAddress
 * @property {string} [address1]
 * @property {string} [address2]
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [zipcode]
 */

/**
 * @typedef {Object} StoreLocationDetailStore
 * @property {string} [displayName]
 * @property {string} [pageUrl]
 * @property {string[]} [hours]
 * @property {StoreLocationDetailAddress} [address]
 */

/**
 * @typedef {Object} StoreLocationDetailOpenParams
 * @property {StoreLocationDetailStore | null} store
 * @property {import('@theme/store-inventory').StoreInventoryStatus} displayStatus
 * @property {string} [statusLabel]
 * @property {boolean} [showCount]
 * @property {number | null} [quantity]
 * @property {Date | null} [lastCheckedAt]
 */

/**
 * @typedef {Object} StoreLocationDetailRefs
 * @property {HTMLElement} [title]
 * @property {HTMLElement} [hoursSummary]
 * @property {HTMLElement} [address]
 * @property {HTMLElement} [availabilityPill]
 * @property {HTMLElement} [statusRow]
 * @property {HTMLElement} [statusDot]
 * @property {HTMLElement} [statusText]
 * @property {HTMLElement} [lastChecked]
 * @property {HTMLAnchorElement} [storeDetailsLink]
 */

/**
 * @extends {Component<StoreLocationDetailRefs>}
 */
class StoreLocationDetail extends Component {
  /**
   * Content refs live inside dialog-component; Horizon's ref map only binds to the nearest Component.
   * @param {string} name
   * @returns {HTMLElement | null}
   */
  #getModalRef(name) {
    return this.querySelector(`#store-location-detail-modal [ref="${name}"]`);
  }

  /**
   * @param {StoreLocationDetailStore | null | undefined} store
   * @returns {string}
   */
  #formatTitle(store) {
    const city = store?.address?.city?.trim();
    const state = store?.address?.state?.trim();
    if (city && state) return `${city}, ${state}`;
    return store?.displayName?.trim() ?? '';
  }

  /**
   * @param {StoreLocationDetailStore | null | undefined} store
   * @returns {string}
   */
  #formatAddressSingleLine(store) {
    const address = store?.address ?? {};
    const parts = [];

    if (address.address1?.trim()) parts.push(address.address1.trim());
    if (address.address2?.trim()) parts.push(address.address2.trim());

    const cityState = [address.city, address.state].filter(Boolean).join(', ');
    const cityStateZip = [cityState, address.zipcode].filter(Boolean).join(' ').trim();
    if (cityStateZip) parts.push(cityStateZip);

    return parts.join(', ');
  }

  /**
   * @param {string} template
   * @param {number} count
   * @returns {string}
   */
  #interpolateCount(template, count) {
    return template.replace(/\{\{\s*count\s*\}\}/g, String(count));
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryStatus} status
   * @param {{ showCount?: boolean; quantity?: number | null }} [options]
   * @returns {string}
   */
  #getStatusCopy(status, options = {}) {
    const { showCount = false, quantity = null } = options;
    const translations = Theme.translations ?? {};
    const loading = translations.store_detail_status_loading ?? 'Checking availability…';
    const unavailable = translations.store_detail_status_unavailable ?? 'Unavailable';

    switch (status) {
      case 'loading':
        return loading;
      case 'in_stock':
        return translations.store_detail_status_in_stock ?? 'Store — In stock';
      case 'low': {
        const base = translations.store_detail_status_low_stock ?? 'Store — Low stock';
        if (showCount && quantity != null) {
          const template =
            translations.store_detail_status_low_stock_count ??
            'Store — Low stock ({{ count }} left)';
          return this.#interpolateCount(template, quantity);
        }
        return base;
      }
      case 'out_of_stock':
        return translations.store_detail_status_out_of_stock ?? 'Store — Out of stock';
      case 'unavailable':
        return unavailable;
      default:
        return unavailable;
    }
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryStatus} status
   */
  #applyStatusPresentation(status) {
    const dotModifiers = [
      'how-to-get-it__status-dot--in_stock',
      'how-to-get-it__status-dot--low',
      'how-to-get-it__status-dot--out_of_stock',
      'how-to-get-it__status-dot--unavailable',
      'how-to-get-it__status-dot--loading',
    ];
    const statusModifiers = [
      'how-to-get-it__status--in_stock',
      'how-to-get-it__status--low',
      'how-to-get-it__status--out_of_stock',
      'how-to-get-it__status--unavailable',
      'how-to-get-it__status--loading',
    ];

    const statusDot = this.#getModalRef('statusDot');
    const statusRow = this.#getModalRef('statusRow');

    if (statusDot instanceof HTMLElement) {
      statusDot.classList.remove(...dotModifiers);
      if (status === 'in_stock') statusDot.classList.add('how-to-get-it__status-dot--in_stock');
      else if (status === 'low') statusDot.classList.add('how-to-get-it__status-dot--low');
      else if (status === 'out_of_stock') statusDot.classList.add('how-to-get-it__status-dot--out_of_stock');
      else if (status === 'unavailable') statusDot.classList.add('how-to-get-it__status-dot--unavailable');
      else statusDot.classList.add('how-to-get-it__status-dot--loading');
    }

    if (statusRow instanceof HTMLElement) {
      statusRow.classList.remove(...statusModifiers);
      if (status === 'in_stock') statusRow.classList.add('how-to-get-it__status--in_stock');
      else if (status === 'low') statusRow.classList.add('how-to-get-it__status--low');
      else if (status === 'out_of_stock') statusRow.classList.add('how-to-get-it__status--out_of_stock');
      else if (status === 'unavailable') statusRow.classList.add('how-to-get-it__status--unavailable');
      else statusRow.classList.add('how-to-get-it__status--loading');
    }
  }

  /**
   * @param {string | null | undefined} openUntilLabel
   * @param {string} fallbackHoursText
   * @param {string | null | undefined} storePageUrl
   */
  #populateHoursRow(openUntilLabel, fallbackHoursText, storePageUrl) {
    const hoursSummary = this.#getModalRef('hoursSummary');
    const openUntilText = this.#getModalRef('openUntilText');
    const hoursSeparator = this.#getModalRef('hoursSeparator');
    const storeDetailsLink = this.#getModalRef('storeDetailsLink');

    const hasOpenUntil = Boolean(openUntilLabel?.trim());
    const pageUrl = storePageUrl?.trim() ?? '';
    const showStoreDetails = hasOpenUntil && Boolean(pageUrl);

    if (openUntilText instanceof HTMLElement) {
      openUntilText.textContent = hasOpenUntil
        ? openUntilLabel.trim()
        : (fallbackHoursText.trim() || '');
    }

    if (hoursSeparator instanceof HTMLElement) {
      hoursSeparator.hidden = !hasOpenUntil;
    }

    if (storeDetailsLink instanceof HTMLAnchorElement) {
      storeDetailsLink.hidden = !showStoreDetails;
      if (showStoreDetails) {
        storeDetailsLink.href = pageUrl;
      } else {
        storeDetailsLink.removeAttribute('href');
      }
    }

    if (hoursSummary instanceof HTMLElement) {
      const hasContent = hasOpenUntil || Boolean(fallbackHoursText.trim());
      hoursSummary.hidden = !hasContent;
    }
  }

  /**
   * @param {Date | null | undefined} date
   * @returns {string}
   */
  #formatLastChecked(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

    const translations = Theme.translations ?? {};
    const template =
      translations.store_detail_last_checked ?? 'Last checked: {{ date }} at {{ time }}';

    const dateFormatter = new Intl.DateTimeFormat(undefined, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    const timeFormatter = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });

    return template
      .replace(/\{\{\s*date\s*\}\}/g, dateFormatter.format(date))
      .replace(/\{\{\s*time\s*\}\}/g, timeFormatter.format(date));
  }

  /**
   * @param {StoreLocationDetailOpenParams} params
   */
  open(params) {
    const { store, displayStatus, statusLabel, showCount, quantity, lastCheckedAt } = params;

    const title = this.#getModalRef('title');
    if (!(title instanceof HTMLElement)) {
      console.warn('[store-location-detail] Modal content refs missing; aborting open.');
      return;
    }

    const translations = Theme.translations ?? {};
    const openUntilTemplate = translations.store_detail_open_until ?? 'Open until {{ time }}';
    const hoursSummary = formatOpenUntilSummary(store?.hours, { openUntilTemplate });

    const address = this.#getModalRef('address');
    const availabilityPill = this.#getModalRef('availabilityPill');
    const statusTextEl = this.#getModalRef('statusText');
    const lastChecked = this.#getModalRef('lastChecked');

    title.textContent = this.#formatTitle(store);

    this.#populateHoursRow(hoursSummary.openUntilLabel, hoursSummary.fallbackHoursText, store?.pageUrl);

    if (address instanceof HTMLElement) {
      const formatted = this.#formatAddressSingleLine(store);
      address.textContent = formatted;
      address.hidden = !formatted;
    }

    const resolvedStatusLabel =
      statusLabel?.trim() ||
      this.#getStatusCopy(displayStatus, { showCount, quantity });

    if (statusTextEl instanceof HTMLElement) {
      statusTextEl.textContent = resolvedStatusLabel;
    }

    if (availabilityPill instanceof HTMLElement) {
      availabilityPill.hidden = displayStatus === 'loading';
    }

    this.#applyStatusPresentation(displayStatus);

    if (lastChecked instanceof HTMLElement) {
      const label = this.#formatLastChecked(lastCheckedAt ?? null);
      lastChecked.textContent = label;
      lastChecked.hidden = !label;
    }

    const modal = document.getElementById('store-location-detail-modal');
    if (modal instanceof DialogComponent) {
      modal.showDialog();
    }
  }
}

if (!customElements.get('store-location-detail')) {
  customElements.define('store-location-detail', StoreLocationDetail);
}

export { StoreLocationDetail };
